import { z } from 'zod';

/**
 * Wiederverwendbare Basis-Schemas.
 *
 * Diese Datei ist bewusst die unterste Ebene der Vertragspyramide: Sie kennt
 * keine Fachlichkeit und darf aus keinem anderen Contracts-Modul importieren.
 * Jede Validierungsregel steht genau hier einmal — dadurch sind Fehlermeldungen
 * über alle Endpunkte hinweg identisch formuliert.
 */

/** RFC-4122-Bezeichner (UUID v4/v7). Alle Entitäts-IDs der Plattform. */
export const uuidSchema = z.string().uuid({ message: 'Muss eine gültige UUID sein.' });

/**
 * Semantische Version ohne führendes "v" (z. B. `1.4.0`, `2.0.0-rc.1`).
 * Wird für `agentVersion` und Blueprint-Versionen benutzt.
 *
 * Bewusst nicht die vollständige SemVer-Referenz-Regex: Deren Zweck ist es,
 * führende Nullen (`01.0.0`) abzulehnen — ein Fall, der bei maschinell
 * erzeugten Agent-Versionen nicht auftritt und den Ausdruck unlesbar macht.
 * Diese Variante prüft Struktur und Zeichenvorrat und bleibt nachvollziehbar.
 */
export const semverSchema = z.string().regex(/^\d+\.\d+\.\d+(?:-[\w.-]+)?(?:\+[\w.-]+)?$/, {
  message: 'Muss eine semantische Version im Format MAJOR.MINOR.PATCH sein.',
});

/**
 * Hostname nach RFC 1123. Erlaubt sind Labels aus Buchstaben, Ziffern und
 * Bindestrichen; ein Label darf weder mit einem Bindestrich beginnen noch enden.
 *
 * Der Wert wird auf Kleinschreibung normalisiert. Hostnamen sind nach RFC 1123
 * ausdrücklich unabhängig von der Groß-/Kleinschreibung — sie deswegen
 * abzulehnen wäre falsch, denn `os.hostname()` liefert auf manchen Systemen
 * Großbuchstaben. Sie unverändert zu übernehmen wäre allerdings ebenso falsch:
 * Der Hostname landet später in Docker-Labels, Dateipfaden und DNS-Einträgen,
 * und diese Kontexte unterscheiden sehr wohl. `Worker-01` und `worker-01`
 * wären dann zwei verschiedene Verzeichnisse für denselben Server.
 *
 * Normalisieren statt ablehnen — das ist der einzige Weg, der beide Fälle
 * richtig behandelt.
 */
export const hostnameSchema = z
  .string()
  .min(1, { message: 'Hostname darf nicht leer sein.' })
  .max(253, { message: 'Hostname darf höchstens 253 Zeichen lang sein.' })
  .regex(/^(?!-)[a-z\d-]{1,63}(?<!-)(\.(?!-)[a-z\d-]{1,63}(?<!-))*$/i, {
    message: 'Muss ein gültiger Hostname nach RFC 1123 sein.',
  })
  .transform((value) => value.toLowerCase());

/** IPv4-Adresse in Punktnotation. */
export const ipv4Schema = z.string().ip({ version: 'v4', message: 'Muss eine IPv4-Adresse sein.' });

/**
 * Zeitstempel als ISO-8601-String mit Zeitzone.
 *
 * Über die Leitung wird bewusst nie ein `Date`-Objekt oder ein Unix-Timestamp
 * transportiert: ISO-8601 ist selbstbeschreibend, in Logs lesbar und
 * eindeutig bezüglich der Zeitzone.
 */
export const isoDateTimeSchema = z.string().datetime({
  offset: true,
  message: 'Muss ein ISO-8601-Zeitstempel mit Zeitzonenangabe sein.',
});

/** Ganzzahl >= 0. */
export const nonNegativeIntSchema = z
  .number()
  .int({ message: 'Muss eine Ganzzahl sein.' })
  .nonnegative({ message: 'Darf nicht negativ sein.' });

/** Ganzzahl > 0. */
export const positiveIntSchema = z
  .number()
  .int({ message: 'Muss eine Ganzzahl sein.' })
  .positive({ message: 'Muss größer als 0 sein.' });

/** Prozentwert von 0 bis 100 (Gleitkomma erlaubt, z. B. CPU-Auslastung). */
export const percentageSchema = z
  .number()
  .min(0, { message: 'Prozentwert darf nicht kleiner als 0 sein.' })
  .max(100, { message: 'Prozentwert darf nicht größer als 100 sein.' });

/**
 * Frei wählbarer Bezeichner für Regionen, Labels und ähnliche Schlüssel.
 * Kleinbuchstaben, Ziffern, Bindestrich — bewusst eng, damit solche Werte
 * gefahrlos in DNS-Namen, Docker-Labels und Dateipfaden auftauchen dürfen.
 */
export const slugSchema = z
  .string()
  .min(1, { message: 'Darf nicht leer sein.' })
  .max(64, { message: 'Darf höchstens 64 Zeichen lang sein.' })
  .regex(/^[a-z\d]+(-[a-z\d]+)*$/, {
    message: 'Nur Kleinbuchstaben, Ziffern und einfache Bindestriche erlaubt.',
  });

/**
 * Korrelations-ID zur Nachverfolgung eines Vorgangs über Master, Queue und
 * Worker hinweg. Taucht in jedem Log-Eintrag beider Anwendungen auf.
 */
export const correlationIdSchema = z
  .string()
  .min(8, { message: 'Korrelations-ID ist zu kurz.' })
  .max(128, { message: 'Korrelations-ID ist zu lang.' });

/** Freie Key-Value-Labels, wie sie der Scheduler zur Node-Auswahl auswertet. */
export const labelsSchema = z.record(slugSchema, z.string().max(256));

/** TCP-Port. */
export const portSchema = z
  .number()
  .int({ message: 'Muss eine Ganzzahl sein.' })
  .min(1, { message: 'Port muss mindestens 1 sein.' })
  .max(65_535, { message: 'Port darf höchstens 65535 sein.' });

/**
 * SHA-256-Prüfsumme in hexadezimaler Kleinschreibung.
 *
 * Wird für den Spec-Hash benutzt, an dem der Master erkennt, ob auf einem Node
 * tatsächlich das läuft, was laufen soll. Die feste Länge ist Teil der Prüfung:
 * Ein abgeschnittener Hash würde still zu einem Dauer-Nichttreffer führen — und
 * damit zu einer Site, die im Sekundentakt neu angewandt wird.
 */
export const sha256HexSchema = z.string().regex(/^[a-f\d]{64}$/, {
  message: 'Muss eine SHA-256-Prüfsumme aus 64 Hexadezimalziffern in Kleinschreibung sein.',
});

export type Uuid = z.infer<typeof uuidSchema>;
export type Semver = z.infer<typeof semverSchema>;
export type IsoDateTime = z.infer<typeof isoDateTimeSchema>;
export type Labels = z.infer<typeof labelsSchema>;
